import type { StyleOptions } from '@dicebear/core';
import type personas from '@dicebear/styles/personas.json';

import type { PersonaAvatarProfile } from '@repo/contracts';

type PersonasOptions = StyleOptions<typeof personas>;

/**
 * A stored profile, in DiceBear's option shape.
 *
 * Typed against the style's own JSON, so a value in `@repo/contracts` that an
 * upgraded style no longer ships is a compile error here rather than a throw
 * at render.
 *
 * Every option is a one-item array: DiceBear treats a list as the set it may
 * pick from, so one entry pins the choice. The seed settles anything the
 * profile does not name. `facialHairProbability` is explicit both ways — left
 * unset, the style gives everybody a 10% chance of a beard, so somebody the
 * model made clean-shaven would sometimes grow one. The beard takes the hair
 * colour, which the seed would otherwise pick independently.
 */
export function toDiceBearOptions(
  profile: PersonaAvatarProfile,
  seed: string,
): PersonasOptions {
  return {
    clothesVariant: [profile.clothes],
    clothingColor: [profile.clothingColor],
    eyesVariant: [profile.eyes],
    facialHairProbability: profile.facialHair ? 100 : 0,
    hairColor: [profile.hairColor],
    hairVariant: [profile.hair],
    mouthVariant: [profile.mouth],
    noseVariant: [profile.nose],
    seed,
    skinColor: [profile.skinColor],
    ...(profile.facialHair
      ? {
          facialHairColor: [profile.hairColor],
          facialHairVariant: [profile.facialHair],
        }
      : {}),
  };
}
